import { createAsyncThunk } from '@reduxjs/toolkit';
import { enqueueSnackbar } from 'notistack';
import type {
  CompetitionApiGetPrivateAllCompetitionsLifecycleEnum,
  CompetitionApiGetPrivateAllCompetitionsTypeOfCompetitionEnum,
  AddCompetitionQuestionsBody,
  AdminUpdateRegistrationDetails,
  AIEnhanceCompetitionDetailsBody,
  AttemptQuestionBody,
  DeleteAssociatedQuestionsBody,
  GenerateCompetitionQuestionsV2Body,
  ReplaceCompetitionQuestionBody,
  ReorderAssociatedQuestionsBody,
  UpsertCompetitionBody,
  CompetitionApiApiCompetitionApiUpsertEventPreferencesRequest,
} from 'src/openapi';
import { getFormattedErr } from 'src/helpers';
import apiClient from 'src/helpers/apiClient';

export const getCompetitionList = createAsyncThunk(
  'competition/getCompetitionList',
  async (_, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getCompetitionApi()
      ).competitionApiGetCompetitionList();
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const getAllCompetitions = createAsyncThunk(
  'competition/getCompetition',
  async (
    {
      pageNo,
      pageSize,
      searchQuery,
      status,
      isRegistered,
      lifecycle,
      typeOfCompetition,
    }: {
      isRegistered?: boolean;
      lifecycle?: CompetitionApiGetPrivateAllCompetitionsLifecycleEnum;
      pageNo?: number;
      pageSize?: number;
      // Discrete pagination (admin) replaces the page; infinite scroll appends.
      replace?: boolean;
      searchQuery?: string;
      status?: string;
      typeOfCompetition?: CompetitionApiGetPrivateAllCompetitionsTypeOfCompetitionEnum;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getCompetitionApi()
      ).competitionApiGetPrivateAllCompetitions({
        isRegistered,
        lifecycle,
        page: pageNo?.toString(),
        pageSize: pageSize?.toString(),
        searchQuery,
        status,
        typeOfCompetition,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const getCompetitionByAliasOrId = createAsyncThunk(
  'competition/getCompetitionById',
  async (
    { competitionAliasOrId }: { competitionAliasOrId: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getCompetitionApi()
      ).competitionApiGetCompetitionById({
        competitionId: competitionAliasOrId,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      // Optional chaining matters here: on a network error or timeout there is no
      // `response`, and reading `.data` off it throws inside the catch.
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const updateCompetitionDetails = createAsyncThunk(
  'competition/updateCompetitionDetails',
  async (
    { payload }: { payload: UpsertCompetitionBody },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getCompetitionApi()
      ).competitionApiUpsertCompetition({ upsertCompetitionBody: payload });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);
let lastBodyCache: AddCompetitionQuestionsBody | null = null;
let lastResponseCache: any = null;

const isSameBody = (a: any, b: any) => JSON.stringify(a) === JSON.stringify(b);
export const addCompetitionQuestionsAdmin = createAsyncThunk(
  'competition/admin/add-competition-questions',
  async (
    {
      competitionId,
      body,
    }: { competitionId: string; body: AddCompetitionQuestionsBody },
    { rejectWithValue },
  ) => {
    try {
      // AI generation is fire-and-forget on the backend; each click must
      // actually enqueue a fresh task, so skip the identical-body cache.
      if (
        !body.generate_with_ai &&
        lastBodyCache &&
        isSameBody(lastBodyCache, body)
      ) {
        return lastResponseCache;
      }

      lastBodyCache = body;

      const response = await (
        await apiClient.getCompetitionApi()
      ).competitionApiAddAdminCompetitionQuestions({
        competitionId,
        addCompetitionQuestionsBody: body,
      });

      lastResponseCache = response.data;
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response?.data);
    }
  },
);

export const getCompetitionQuestions = createAsyncThunk(
  'competition/get-competition-questions',
  async (
    {
      competitionId,
      pageNo,
      pageSize,
      silent,
    }: {
      competitionId: string;
      pageNo: number;
      pageSize: number;
      // A background resync: no loader over the list, no error toast.
      silent?: boolean;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getCompetitionApi()
      ).competitionApiGetAdminCompetitionQuestions({
        competitionId,
        pageNo,
        pageSize,
      });

      return response.data;
    } catch (err: any) {
      if (!silent) {
        enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      }
      return rejectWithValue(err.response?.data);
    }
  },
);

export const registerInCompetition = createAsyncThunk(
  'competition/register',
  async (
    {
      competitionId,
      userResumeId,
      selfieBase64,
    }: {
      competitionId: string;
      userResumeId?: string | null;
      selfieBase64?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getCompetitionApi()
      ).competitionApiCompetitionRegistrations({
        competitionId,
        competitionRegistrationBody: {
          user_resume_id: userResumeId,
          selfie_base64: selfieBase64,
        },
      });
      return response.data;
    } catch (err: any) {
      const errorKey = err?.response?.data?.error;
      // These blocks are explained inline in a dialog, so skip the generic toast:
      // - face_already_registered_different_account (face-capture dialog)
      // - no_matching_skills (hiring-event "complete your profile" dialog)
      if (
        errorKey !== 'face_already_registered_different_account' &&
        errorKey !== 'no_matching_skills'
      ) {
        enqueueSnackbar(getFormattedErr(errorKey));
      }
      return rejectWithValue(err.response?.data);
    }
  },
);

export const getCompetitionRegistrations = createAsyncThunk(
  'competition/registerations',
  async (
    {
      competitionId,
      pageNo,
      pageSize,
      searchQuery,
      sortBy,
      sortOrder,
    }: {
      competitionId: string;
      pageNo: number;
      pageSize: number;
      searchQuery?: string;
      sortBy?: string;
      sortOrder?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getCompetitionApi()
      ).competitionApiGetCompetitionRegistrations({
        competitionId,
        page: pageNo,
        pageSize,
        searchQuery,
        sortBy,
        sortOrder,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const getCompetitionLeaderboard = createAsyncThunk(
  'competition/leaderboard',
  async (
    {
      competitionId,
      pageNo,
      pageSize,
    }: { competitionId: string; pageNo?: number; pageSize?: number },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getCompetitionApi()
      ).competitionApiGetCompetitionLeaderboard({
        competitionId,
        page: pageNo,
        pageSize,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const scoreCompetitionEntries = createAsyncThunk(
  'competition/score',
  async (
    { id, scores }: { id: string; scores: AdminUpdateRegistrationDetails },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getCompetitionApi()
      ).competitionApiReviewCompetitionEntries({
        competitionId: id,
        adminUpdateRegistrationDetails: scores,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const deleteCompetition = createAsyncThunk(
  'competition/delete',
  async ({ id }: { id: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getCompetitionApi()
      ).competitionApiDeleteCompetition({ competitionId: id });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const submitCompetition = createAsyncThunk(
  'competition/submitEntry',
  async (
    {
      competitionId,
      title,
      description,
      project_url,
      project_file,
      submission_data,
      sessionId,
      entryId,
    }: {
      competitionId: string;
      title?: string;
      description?: string;
      project_url?: string;
      project_file?: any;
      submission_data?: string;
      sessionId?: string;
      entryId?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getCompetitionApi()
      ).competitionApiCompetitionRegistrationsEntrySubmit({
        competitionId,
        title,
        description,
        projectUrl: project_url,
        projectFile: project_file,
        submissionData: submission_data,
        sessionId,
        entryId,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const getCompetitionStartStatistics = createAsyncThunk(
  'get/competition/start-statistics',
  async ({ competitionId }: { competitionId: string }, { rejectWithValue }) => {
    try {
      const res = await (
        await apiClient.getCompetitionApi()
      ).competitionApiGetCompetitionStartStatistics({ competitionId });
      return res.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const getCompetitionUnansweredQuestion = createAsyncThunk(
  'get/competition/unanwered-question',
  async ({ sessionId }: { sessionId: string }, { rejectWithValue }) => {
    try {
      const res = await (
        await apiClient.getQuestionApi()
      ).questionApiGetUnansweredQuestionSkillBuilder({ sessionId });
      return res.data;
    } catch (err: any) {
      const errKey = err?.response?.data?.error;
      // Both are normal end-of-session signals — no toast.
      if (
        errKey !== 'session_question_not_found' &&
        errKey !== 'session_already_complete'
      ) {
        enqueueSnackbar(getFormattedErr(errKey));
      }
      return rejectWithValue(err.response.data);
    }
  },
);

export const attemptCompetitionQuestion = createAsyncThunk(
  'competition/attempt-question',
  async (
    {
      competitionId,
      questionId,
      payload,
    }: {
      competitionId: string;
      questionId: string;
      payload: AttemptQuestionBody;
    },
    { rejectWithValue },
  ) => {
    try {
      const res = await (
        await apiClient.getQuestionApi()
      ).questionApiAttemptQuestionByCompetition({
        competitionId,
        questionId,
        attemptQuestionBody: payload,
      });
      return res.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const fetchCompetitionUserAnalyticsDetails = createAsyncThunk(
  'get/competition/user-analytics-details',
  async ({ competitionId }: { competitionId: string }, { rejectWithValue }) => {
    try {
      const res = await (
        await apiClient.getCompetitionApi()
      ).competitionApiGetDetailedCompetitionAnalyticsByUser({ competitionId });
      return res.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const fetchCompetitionAttemptHistory = createAsyncThunk(
  'get/competition/attempt-history',
  async (
    {
      competitionId,
      pageNo,
      pageSize,
    }: {
      competitionId: string;
      pageNo?: number;
      pageSize?: number;
    },
    { rejectWithValue },
  ) => {
    try {
      const res = await (
        await apiClient.getQuestionApi()
      ).questionApiGetAnsweredQuestionHistoryList({
        competitionId,
        page: pageNo?.toString(),
        pageSize: pageSize?.toString(),
        skillId: undefined,
      });
      return res.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const getCompetitionReferredUsers = createAsyncThunk(
  'get/competition/referred-users',
  async (
    {
      competitionId,
      pageNo,
      pageSize,
    }: {
      competitionId: string;
      pageNo?: number;
      pageSize?: number;
    },
    { rejectWithValue },
  ) => {
    try {
      const res = await (
        await apiClient.getCompetitionApi()
      ).competitionApiGetUserReferralByCompetition({
        competitionId,
        pageNo,
        pageSize,
        totalPages: undefined,
      });
      return res.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const deleteAssociatedQuestionById = createAsyncThunk(
  'competition/delete-associated-question',
  async (
    {
      competitionId,
      questionBody,
      silent = false,
    }: {
      competitionId: string;
      questionBody: DeleteAssociatedQuestionsBody;
      silent?: boolean;
    },
    { rejectWithValue },
  ) => {
    try {
      const res = await (
        await apiClient.getCompetitionApi()
      ).competitionApiDeleteAssociatedQuestions({
        competitionId,
        deleteAssociatedQuestionsBody: questionBody,
      });
      if (!silent) {
        enqueueSnackbar('Deleted successfully', { variant: 'success' });
      }
      return res.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);
export const reorderAssociatedQuestions = createAsyncThunk(
  'competition/reorder-associated-questions',
  async (
    {
      competitionId,
      reorderedQuestions,
    }: {
      competitionId: string;
      reorderedQuestions: ReorderAssociatedQuestionsBody;
    },
    { rejectWithValue },
  ) => {
    try {
      const res = await (
        await apiClient.getCompetitionApi()
      ).competitionApiReorderCompetitionQuestionsMapping({
        competitionId,
        reorderAssociatedQuestionsBody: reorderedQuestions,
      });
      enqueueSnackbar('Reordered successfully', { variant: 'success' });
      return res.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);
export const associatedManualQuestions = createAsyncThunk(
  'competition/associated-manual-questions',
  async (
    {
      competitionId,
      reorderedQuestions,
    }: {
      competitionId: string;
      reorderedQuestions: ReorderAssociatedQuestionsBody;
    },
    { rejectWithValue },
  ) => {
    try {
      const res = await (
        await apiClient.getCompetitionApi()
      ).competitionApiAssociateManualQuestions({
        competitionId,
        reorderAssociatedQuestionsBody: reorderedQuestions,
      });
      return res.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const highlightJob = createAsyncThunk(
  'job/highlight-job',
  async (
    {
      jobId,
    }: {
      jobId: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const res = await (
        await apiClient.getJobApi()
      ).jobApiHighlightJob({ jobId });
      return res.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);
export const getCandidatesAnsweredQuestionsAndAnalytics = createAsyncThunk(
  'question/get-candidates-answered-questions',
  async (
    {
      userId,
      competitionId,
      page,
      pageSize,
    }: {
      userId: string;
      competitionId: string;
      page: number;
      pageSize: number;
    },
    { rejectWithValue },
  ) => {
    try {
      const res = await (
        await apiClient.getCompetitionApi()
      ).competitionApiGetCompetitionUserAnsweredQuestionsAnalytics({
        competitionId,
        examId: undefined,
        filterType: undefined,
        page,
        pageSize,
        searchString: undefined,
        skillIds: undefined,
        userId,
      });
      return res.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const addCompetitionQuestions = createAsyncThunk(
  'competition/add-competition-questions',
  async (
    {
      competitionId,
      body,
    }: { competitionId: string; body: AddCompetitionQuestionsBody },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getCompetitionApi()
      ).competitionApiAddCompetitionQuestions({
        competitionId,
        addCompetitionQuestionsBody: body,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response?.data);
    }
  },
);

export const lockCompetition = createAsyncThunk(
  'competition/lock',
  async ({ competitionId }: { competitionId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getCompetitionApi()
      ).competitionApiLockCompetition({ competitionId });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const unlockCompetition = createAsyncThunk(
  'competition/unlock',
  async (
    { competitionId, password }: { competitionId: string; password: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getCompetitionApi()
      ).competitionApiUnlockCompetition({
        competitionId,
        unlockCompetitionBody: { password },
      });
      enqueueSnackbar('Competition unlocked successfully', {
        variant: 'success',
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const enhanceCompetitionDetails = createAsyncThunk(
  'competition/ai-enhance-details',
  async (body: AIEnhanceCompetitionDetailsBody, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getCompetitionApi()
      ).competitionApiAiEnhanceCompetitionDetails({
        aIEnhanceCompetitionDetailsBody: body,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response?.data);
    }
  },
);

export const redeemCompetitionPremiumProfile = createAsyncThunk(
  'competition/redeem-premium-profile',
  async ({ competitionId }: { competitionId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getCompetitionApi()
      ).competitionApiRedeemCompetitionPremiumProfile({ competitionId });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const generateCompetitionQuestionsV2 = createAsyncThunk(
  'competition/generate-questions-v2',
  async (
    {
      competitionId,
      body,
    }: { competitionId: string; body: GenerateCompetitionQuestionsV2Body },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getCompetitionApi()
      ).competitionApiGenerateCompetitionQuestionsV2({
        competitionId,
        generateCompetitionQuestionsV2Body: body,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response?.data);
    }
  },
);

export const replaceCompetitionQuestion = createAsyncThunk(
  'competition/replace-question',
  async (
    {
      competitionId,
      body,
    }: {
      competitionId: string;
      body: ReplaceCompetitionQuestionBody;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getCompetitionApi()
      ).competitionApiReplaceCompetitionQuestionV2({
        competitionId,
        replaceCompetitionQuestionBody: body,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response?.data);
    }
  },
);

export const getCalibrationStatus = createAsyncThunk(
  'competition/calibration-status',
  async ({ competitionId }: { competitionId: string }, { rejectWithValue }) => {
    try {
      const res = await (
        await apiClient.getCompetitionApi()
      ).competitionApiGetCalibrationStatus({ competitionId });
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data);
    }
  },
);

// Persist the candidate's chosen ≤4 skills for the event.
export const setCalibrationSkills = createAsyncThunk(
  'competition/calibration-set-skills',
  async (
    { competitionId, skillIds }: { competitionId: string; skillIds: string[] },
    { rejectWithValue },
  ) => {
    try {
      const res = await (
        await apiClient.getCompetitionApi()
      ).competitionApiSetCalibrationSkills({
        competitionId,
        setCalibrationSkillsBody: { skill_ids: skillIds },
      });
      return res.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response?.data);
    }
  },
);

// Start (or resume) calibration for ONE skill; returns that skill's session id.
export const startCalibrationSession = createAsyncThunk(
  'competition/calibration-start',
  async (
    { competitionId, skillId }: { competitionId: string; skillId: string },
    { rejectWithValue },
  ) => {
    try {
      const res = await (
        await apiClient.getCompetitionApi()
      ).competitionApiStartCalibrationSession({
        competitionId,
        startCalibrationBody: { skill_id: skillId },
      });
      return res.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response?.data);
    }
  },
);

export const saveEventPreferences = createAsyncThunk(
  'competition/event-preferences-save',
  async (
    body: CompetitionApiApiCompetitionApiUpsertEventPreferencesRequest,
    { rejectWithValue },
  ) => {
    try {
      const res = await (
        await apiClient.getCompetitionApi()
      ).competitionApiUpsertEventPreferences({
        competitionId: body.competitionId,
        eventPreferencesBody: body.eventPreferencesBody,
      });
      return res.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err.response?.data);
    }
  },
);
